/**
 * 视频模型选择器
 * 顶部一排公司 logo 选项，选中哪家公司就只展示该公司下的模型
 */

import { useEffect, useMemo, useState } from 'react';
import type { VideoModelPublicInfo } from '../services/videoApi';
import { CompanyLogo } from '../../../components/CompanyLogo';

interface VideoModelSelectorProps {
  value: string;
  onChange: (modelId: string) => void;
  models: VideoModelPublicInfo[];
}

interface ModelCompanyGroup {
  key: string;
  label: string;
  models: VideoModelPublicInfo[];
}

/**
 * 按公司分组；组间顺序跟随模型 sortOrder 首次出现的顺序（管理员可通过排序控制），
 * 无公司信息的模型归入「其他」兜底组。
 */
function groupModelsByCompany(models: VideoModelPublicInfo[]): ModelCompanyGroup[] {
  const groups: ModelCompanyGroup[] = [];
  const byKey = new Map<string, ModelCompanyGroup>();
  for (const model of models) {
    const key = model.company?.id || 'other';
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: model.company?.label || '其他', models: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.models.push(model);
  }
  return groups;
}

/**
 * 起价：该模型所有「可用模式 × 可用分辨率」组合中 ceil 后的最低整数价。
 * 与实扣口径一致（后端对每条任务整体向上取整，余额与流水均为整数积分），
 * 也与分辨率选择器按钮上的取整价一致；不含音频倍率（不开音频才是最低价）。
 */
function getStartingPrice(model: VideoModelPublicInfo): { value: number; unit: string } {
  const base = model.billingMode === 'per_call' ? model.creditsPerCall : model.creditsPerSecond;
  const unit = model.billingMode === 'per_call' ? '次' : '秒';

  let min = Math.max(1, Math.ceil(base));
  for (const modeInfo of model.modes) {
    const modeMul = model.modeMultipliers?.[modeInfo.mode] ?? 1;
    for (const res of modeInfo.supportedResolutions) {
      const resMul = model.resolutionMultipliers?.[res] ?? 1;
      const price = Math.max(1, Math.ceil(base * resMul * modeMul));
      if (price < min) min = price;
    }
  }
  return { value: min, unit };
}

export function VideoModelSelector({ value, onChange, models }: VideoModelSelectorProps) {
  const groups = useMemo(() => groupModelsByCompany(models), [models]);

  // 当前选中模型所属公司（用于选项栏初始定位与外部切换跟随）
  const valueCompanyId = models.find((m) => m.modelId === value)?.company?.id ?? null;
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(valueCompanyId);

  // 仅在所选模型的公司变化时跟随（用户自行切换选项卡浏览时不打断）
  useEffect(() => {
    if (valueCompanyId) setActiveCompanyId(valueCompanyId);
  }, [valueCompanyId]);

  if (models.length === 0) {
    return (
      <div className="text-sm text-light-muted bg-light-hover rounded-xl px-3 py-3">
        暂无可用的视频模型，请联系管理员配置
      </div>
    );
  }

  const activeGroup = groups.find((g) => g.key === activeCompanyId) ?? groups[0];

  return (
    <div>
      <div className="text-sm font-medium text-light-text mb-2">模型</div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {groups.map((group) => {
          const isActive = group.key === activeGroup?.key;
          return (
            <button
              key={group.key}
              type="button"
              onClick={() => {
                setActiveCompanyId(group.key);
                // 切到新公司时默认选中该公司第一个模型（当前模型已属该公司则不重复触发）
                const first = group.models[0];
                if (first && !group.models.some((m) => m.modelId === value)) {
                  onChange(first.modelId);
                }
              }}
              title={group.label}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-all ${
                isActive
                  ? 'border-accent-blue bg-accent-blue/5 text-accent-blue'
                  : 'border-light-border bg-white text-light-text hover:border-accent-blue'
              }`}
            >
              <CompanyLogo companyId={group.key} label={group.label} size={16} />
              <span className="text-xs font-medium">{group.label}</span>
            </button>
          );
        })}
      </div>
      <div className="grid gap-2">
        {activeGroup?.models.map((model) => {
          const isActive = model.modelId === value;
          const billingBadgeLabel = model.billingMode === 'per_call' ? '按次' : '按秒';
          const startingPrice = getStartingPrice(model);
          return (
            <button
              key={model.modelId}
              type="button"
              onClick={() => onChange(model.modelId)}
              className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                isActive
                  ? 'border-accent-blue bg-accent-blue/5 shadow-md'
                  : 'border-light-border bg-white hover:border-accent-blue'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <CompanyLogo
                    companyId={model.company?.id || activeGroup?.key || 'other'}
                    label={model.company?.label || activeGroup?.label || '其他'}
                    size={14}
                  />
                  <span
                    className={`font-medium text-sm truncate ${
                      isActive ? 'text-accent-blue' : 'text-light-text'
                    }`}
                  >
                    {model.name}
                  </span>
                  <span
                    className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
                      model.billingMode === 'per_call'
                        ? isActive
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-amber-50 text-amber-700'
                        : isActive
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-sky-50 text-sky-700'
                    }`}
                  >
                    {billingBadgeLabel}
                  </span>
                </div>
                <span className="text-xs text-light-muted">
                  {startingPrice.value} 积分/{startingPrice.unit}起
                </span>
              </div>
              {model.description && (
                <p className="text-xs text-light-muted mt-1">{model.description}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
